import React from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Box, HStack, Icon, Text } from '@chakra-ui/react';
import type { IconType } from 'react-icons';
import { Tooltip } from '@/components/ui/tooltip';

/** Shared by the button and the statement, so the two read as the same thing either way. */
const CHIP_HEIGHT = 8;

/**
 * Long enough that crossing the chip on the way somewhere else does not summon a label.
 *
 * These sit in pairs, so the shorter delay meant sliding from one to the other fired both.
 */
const TOOLTIP_OPEN_DELAY_MS = 700;
const CHIP_PADDING_X = 3;

interface MaintenanceScheduleChipProps {
	icon?: IconType;
	value: string;
	/** Shorter label shown below the desktop breakpoint. */
	mobileValue?: string;
	/** Removes the control-like outline when the value is explanatory metadata rather than an available action. */
	borderless?: boolean;
	/**
	 * What activating it does — "Reschedule task, currently 12 August 2026". The chip shows only
	 * its value, so the name has to say what the caption used to.
	 */
	ariaLabel?: string;
	/** Short action description shown on hover and keyboard focus. */
	tooltip?: string;
	/**
	 * Withhold the tooltip while the chip's own editor is open.
	 *
	 * The tooltip and the popover anchor to the same box, so an open editor gets a label floating over
	 * it describing the press that opened it.
	 */
	tooltipDisabled?: boolean;
	/** An explanation for a static value. Omit when the value needs no additional context. */
	note?: string;
	/** Absent leaves the chip a statement rather than a control, for a task that can't be edited. */
	onSelect?: () => void;
}

/**
 * When a task is due and how often it repeats, sitting under its title. Pill-shaped and sized to
 * its own content rather than a full-width row: these are the task's defining facts, and a panel
 * apiece would give them more of the header than the title itself.
 */
const ScheduleChip: React.FC<MaintenanceScheduleChipProps> = ({
	icon,
	value,
	mobileValue,
	borderless = false,
	ariaLabel,
	tooltip,
	tooltipDisabled = false,
	note,
	onSelect,
}) => {
	const hasDistinctMobileValue = mobileValue !== undefined && mobileValue !== value;
	const content = (
		<>
			{icon ? <Icon as={icon} boxSize={4} color="fg.muted" flexShrink={0} /> : null}
			{hasDistinctMobileValue ? (
				<>
					<Text display={{ base: 'block', md: 'none' }} truncate>
						{mobileValue}
					</Text>
					<Text display={{ base: 'none', md: 'block' }} truncate>
						{value}
					</Text>
				</>
			) : (
				<Text truncate>{value}</Text>
			)}
		</>
	);

	if (!onSelect) {
		return (
			<HStack
				role={note ? 'note' : undefined}
				aria-label={note}
				title={note}
				gap={2}
				h={CHIP_HEIGHT}
				px={CHIP_PADDING_X}
				minW={0}
				borderWidth={borderless ? 0 : '1px'}
				borderColor="border.default"
				borderRadius="full"
				bg={borderless ? 'surface.subtle' : 'surface.default'}
				textStyle="label"
			>
				{content}
			</HStack>
		);
	}

	return (
		<Tooltip content={tooltip ?? ariaLabel ?? value} openDelay={TOOLTIP_OPEN_DELAY_MS} disabled={tooltipDisabled}>
			<Box as="span" display="inline-flex" minW={0}>
				<OutlinedButton
					// Raised, since it is one: the statement form beside it is the same shape flat, and the
					// shadow is what separates a control from a chip that only reads.
					elevated
					h={CHIP_HEIGHT}
					px={CHIP_PADDING_X}
					// The default 96px floor is for footer actions standing alone; here the chips sit together
					// and one padded out past its own text reads as a gap rather than a shape.
					minW={0}
					aria-label={ariaLabel}
					_focusVisible={{ outline: '2px solid', outlineColor: 'brand.solid', outlineOffset: '2px' }}
					onClick={onSelect}
				>
					{content}
				</OutlinedButton>
			</Box>
		</Tooltip>
	);
};

export default ScheduleChip;
